<template>
  <div class="navigation-title-section">
    <div class="navigation-title-section-content">Trust Signals</div>
  </div>
  <div class="trust-page">
    <p class="intro-text">
      Trust signals show directly under the Add to cart button on the artwork page.
      <strong>Global</strong> signals are ticked by default on every artwork that has
      "Include global trust signals" on. <strong>Per-artwork</strong> signals start off
      and appear only where you tick them. Either kind can be unticked or reordered on
      a single artwork, from the Trust Signals tab of the artwork edit screen — the
      order below is only the default.
    </p>

    <div class="row">
      <div class="col-12 col-md-4">
        <div class="form-box">
          <div class="form-box-header">
            <div class="form-box-title">
              <template v-if="mode === 'create'">New trust signal</template>
              <template v-else>Editing “{{ editingLabel }}”</template>
            </div>
            <el-button v-if="mode === 'edit'" size="small" @click="resetForm">
              + New signal
            </el-button>
          </div>

          <label for="label">Label</label>
          <el-input
            v-model="form.label"
            id="label"
            placeholder="Certificate of authenticity"
            class="mb-2"
          ></el-input>

          <label>Icon</label>
          <div class="icon-picker mb-2">
            <ImageSelectorInput
              :key="`icon-${editingId || 'new'}-${iconResetKey}`"
              v-model="form.icon_media_id"
              :multiple="false"
              :featuredImageSlug="editingIconUrl || 'empty'"
              :pdfSelector="false"
            ></ImageSelectorInput>
            <el-button v-if="hasIcon" @click="clearIcon">Remove icon</el-button>
          </div>
          <p class="hint">
            Upload a 26&times;26 SVG with the stroke colour set to
            <code>#474638</code> — icons are drawn as-is. Optional; a signal
            without an icon shows as text only.
          </p>

          <label for="link-url">Link (optional)</label>
          <el-input
            v-model="form.link_url"
            id="link-url"
            placeholder="mailto:advisor@collectceres.com"
            class="mb-2"
          ></el-input>
          <p class="hint">
            Turns the row into a link. Use this for a "Talk to an advisor" row.
          </p>

          <label>Type</label>
          <div class="type-row mb-2">
            <el-radio-group v-model="form.is_default">
              <el-radio :label="true">Global</el-radio>
              <el-radio :label="false">Per artwork</el-radio>
            </el-radio-group>
          </div>
          <p class="hint">
            Global: ticked by default on every artwork that includes global signals.
            Per artwork: starts off, and shows only where you tick it. Both can be
            adjusted per artwork afterwards.
          </p>
          <div class="checkbox-row">
            <el-checkbox v-model="form.is_active">Visible on the site</el-checkbox>
            <p class="hint hint-under">
              Off hides this signal from every artwork at once without deleting it —
              it overrides the setting above and any per-artwork choice.
            </p>
          </div>

          <div class="form-actions">
            <el-button
              class="button_style_1"
              :loading="submitting"
              @click="createOrUpdate"
            >
              {{ mode === "create" ? "Create" : "Update" }}
            </el-button>
            <el-button v-if="mode === 'edit'" @click="resetForm">Cancel edit</el-button>
          </div>
        </div>
      </div>

      <div class="col-12 col-md-8">
        <div class="list-header">
          <span class="list-title">{{ signals.length }} signal(s)</span>
          <el-button
            v-if="orderDirty"
            class="button_style_1"
            :loading="savingOrder"
            @click="saveOrder"
          >
            Save order
          </el-button>
        </div>

        <p v-if="signals.length === 0" class="empty-notice">
          No trust signals yet. Create one on the left.
        </p>

        <draggable
          v-else
          :list="signals"
          item-key="id"
          class="signal-list"
          ghost-class="ghost-row"
          handle=".drag-handle"
          @update="onOrderChanged"
        >
          <template #item="{ element: row, index: i }">
            <div
              class="signal-row"
              :class="{ 'is-inactive': !row.is_active, 'is-editing': row.id === editingId }"
            >
              <span class="position-num">{{ i + 1 }}</span>
              <span class="drag-handle" title="Drag to reorder">
                <font-awesome-icon :icon="['fas', 'arrows-alt']" />
              </span>
              <img
                v-if="row.icon && row.icon.slug"
                :src="previewDomain + row.icon.slug"
                class="signal-icon"
                alt=""
              />
              <span v-else class="signal-icon-empty" title="No icon" />
              <span class="signal-label">{{ row.label }}</span>
              <span v-if="row.link_url" class="badge badge-link" :title="row.link_url">link</span>
              <span v-if="row.is_default" class="badge badge-global">global</span>
              <span v-else class="badge">per artwork</span>
              <span v-if="!row.is_active" class="badge badge-off">hidden</span>
              <div class="danger-area">
                <p @click="edit(row)">Edit</p>
                <p class="delete" @click="remove(row)">Delete</p>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { ElMessageBox, ElNotification } from "element-plus";
import ImageSelectorInput from "../../components/Tiptap/ImageSelectorInput";

const emptyForm = () => ({
  label: "",
  icon_media_id: "",
  link_url: "",
  is_default: true,
  is_active: true,
});

export default {
  name: "TrustSignals",
  components: { draggable, ImageSelectorInput },
  data() {
    return {
      signals: [],
      editingIconUrl: null,
      editingLabel: "",
      iconResetKey: 0,
      form: emptyForm(),
      mode: "create",
      editingId: null,
      submitting: false,
      savingOrder: false,
      orderDirty: false,
    };
  },
  computed: {
    previewDomain() {
      return process.env.VUE_APP_SHARED_API;
    },
    hasIcon() {
      return !!this.form.icon_media_id || !!this.editingIconUrl;
    },
  },
  created() {
    this.loadSignals();
  },
  mounted() {
    // FileSelector never loads the media library itself — every page that uses
    // it populates the store first (see Widgets.vue, NewItem.vue, CustomTax.vue).
    // Without this the Images tab renders empty and nothing can be selected.
    this.$store.dispatch("setMediaData", "image");
  },
  methods: {
    loadSignals() {
      this.$api
        .get(`${process.env.VUE_APP_SHARED_API}api/trust-signals?per_page=200`)
        .then((res) => {
          this.signals = res.data.data || [];
          this.orderDirty = false;
        })
        .catch(() => {
          ElNotification({
            title: "Error",
            message: "Could not load trust signals.",
            type: "error",
          });
        });
    },
    clearIcon() {
      this.form.icon_media_id = "";
      this.editingIconUrl = null;
      this.iconResetKey += 1;
    },
    resetForm() {
      this.form = emptyForm();
      this.mode = "create";
      this.editingId = null;
      this.editingIconUrl = null;
      // ImageSelectorInput keeps the chosen image in its own state, so it has to
      // be remounted via :key — clearing form.icon_media_id is not enough.
      this.iconResetKey += 1;
    },
    edit(row) {
      this.mode = "edit";
      this.editingId = row.id;
      this.editingLabel = row.label;
      this.editingIconUrl = row.icon?.slug ? this.previewDomain + row.icon.slug : null;
      this.form = {
        label: row.label || "",
        icon_media_id: row.icon_media_id || "",
        link_url: row.link_url || "",
        is_default: !!row.is_default,
        is_active: !!row.is_active,
      };
    },
    payload() {
      return {
        label: this.form.label,
        icon_media_id: this.form.icon_media_id || null,
        link_url: this.form.link_url || null,
        is_default: this.form.is_default ? "1" : "0",
        is_active: this.form.is_active ? "1" : "0",
      };
    },
    createOrUpdate() {
      if (!this.form.label) {
        ElNotification({
          title: "Warning!",
          message: "Label is required.",
          type: "warning",
        });
        return;
      }

      this.submitting = true;
      const base = `${process.env.VUE_APP_SHARED_API}api/trust-signals`;
      const request =
        this.mode === "create"
          ? this.$api.post(base, this.payload())
          : this.$api.put(`${base}/${this.editingId}`, this.payload());

      request
        .then(() => {
          ElNotification({
            title: "Success!",
            message: this.mode === "create" ? "Trust signal created." : "Trust signal updated.",
            type: "success",
          });
          this.resetForm();
          this.loadSignals();
        })
        .catch((e) => {
          ElNotification({
            title: "Error",
            message: e.response?.data?.errors?.label?.[0] || "Could not save trust signal.",
            type: "error",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    remove(row) {
      ElMessageBox.confirm(
        `Delete "${row.label}"? It will be removed from every artwork.`,
        "Warning",
        { confirmButtonText: "Delete", cancelButtonText: "Cancel", type: "warning" }
      )
        .then(() => {
          this.$api
            .delete(`${process.env.VUE_APP_SHARED_API}api/trust-signals/${row.id}`)
            .then(() => {
              ElNotification({
                title: "Success!",
                message: "Trust signal deleted.",
                type: "success",
              });
              if (this.editingId === row.id) this.resetForm();
              this.loadSignals();
            })
            .catch(() => {
              ElNotification({
                title: "Error",
                message: "Could not delete trust signal.",
                type: "error",
              });
            });
        })
        .catch(() => {});
    },
    onOrderChanged() {
      this.orderDirty = true;
    },
    saveOrder() {
      this.savingOrder = true;
      this.$api
        .post(`${process.env.VUE_APP_SHARED_API}api/trust-signals/reorder`, {
          trust_signals: this.signals.map((s) => s.id),
        })
        .then(() => {
          ElNotification({ title: "Saved!", message: "Order saved.", type: "success" });
          this.orderDirty = false;
        })
        .catch(() => {
          ElNotification({
            title: "Error",
            message: "Could not save order.",
            type: "error",
          });
        })
        .finally(() => {
          this.savingOrder = false;
        });
    },
  },
};
</script>

<style scoped>
.trust-page {
  padding: 16px;
}
.intro-text {
  color: #555;
  margin-bottom: 16px;
}
.full-width {
  width: 100%;
}
.mb-2 {
  margin-bottom: 8px;
}
.hint {
  font-size: 12px;
  color: #888;
  margin: 0 0 8px;
}

/* Form */
.form-box {
  border: 1px solid #e0e0e0;
  background: #fff;
  padding: 14px;
}
.form-box-title {
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.checkbox-row {
  margin-top: 10px;
}
.form-actions {
  margin-top: 14px;
  display: flex;
  gap: 8px;
}
.icon-picker :deep(.featured_image_single) {
  max-width: 52px;
  max-height: 52px;
  object-fit: contain;
  margin-bottom: 10px;
}
/* The shared picker's own remove button says "Remove featured image", which
   makes no sense under a field called Icon — hidden in favour of ours. */
.icon-picker :deep(.button_style_2) {
  display: none;
}
.hint-under {
  margin: 2px 0 0 24px;
}
code {
  background: #f4f4f5;
  padding: 0 3px;
}

/* List */
.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  min-height: 32px;
}
.list-title {
  font-size: 13px;
  color: #888;
}
.empty-notice {
  color: #888;
}
.signal-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.signal-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 6px;
  border: 1px solid #e0e0e0;
  background: #fff;
  position: relative;
}
.signal-row.is-inactive {
  opacity: 0.6;
}
.signal-row.is-editing {
  border-color: #1f3c2a;
  box-shadow: inset 3px 0 0 #1f3c2a;
}
.form-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.position-num {
  min-width: 24px;
  font-size: 12px;
  color: #888;
  text-align: center;
  font-weight: 600;
}
.drag-handle {
  flex-shrink: 0;
  cursor: grab;
  color: #909399;
  display: flex;
  align-items: center;
  padding: 4px 6px;
  user-select: none;
}
.drag-handle:active {
  cursor: grabbing;
}
.signal-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}
.signal-icon-empty {
  width: 26px;
  height: 26px;
  border: 1px dashed #dcdfe6;
  flex-shrink: 0;
}
.signal-label {
  flex: 1;
  font-size: 13px;
  min-width: 0;
}
.badge {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border: 1px solid #dcdfe6;
  color: #909399;
  flex-shrink: 0;
}
.badge-link {
  border-color: #1f3c2a;
  color: #1f3c2a;
}
.badge-global {
  border-color: #1f3c2a;
  color: #1f3c2a;
  background: #f0f4f1;
}
.type-row {
  padding: 2px 0;
}
.badge-off {
  border-color: #e53e3e;
  color: #e53e3e;
}
.danger-area {
  display: flex;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.signal-row:hover .danger-area {
  opacity: 1;
}
.danger-area p {
  margin: 0;
  font-size: 12px;
  cursor: pointer;
  color: #1f3c2a;
}
.danger-area p.delete {
  color: #e53e3e;
}
.ghost-row {
  opacity: 0.5;
}
</style>
